import React from "react"
import { Col, Button, Modal, Form } from "react-bootstrap"
import { Formik } from "formik"
import { string, object } from "yup"
import { useTranslation } from "react-i18next"
import IconPicker from "../../../../components/iconPicker"

type GuideFormValues = {
  icon: string
  titleEn: string
  titleFi: string
  isDraft: boolean
}

type GuideModalData = {
  icon?: string | null
  titleEn?: string | null
  titleFi?: string | null
  isDraft?: boolean | null
}

type GuideModalProps = {
  show: boolean
  data?: GuideModalData | null
  onSubmit: (values: Record<string, string>) => void
  onCancel: () => void
}

const validationSchema = (t: (key: string) => string) => {
  return object().shape(
    {
      titleFi: string()
        .when(["titleEn"], {
          is: (titleEn: string) => !titleEn,
          then: () => string().required(t("common.validation.eitherRequired")),
        })
        .max(160, t("common.validation.invalidMaxTopicLength")),
      titleEn: string()
        .when(["titleFi"], {
          is: (titleFi: string) => !titleFi,
          then: () => string().required(t("common.validation.eitherRequired")),
        })
        .max(160, t("common.validation.invalidMaxTopicLength")),
      icon: string().required(t("common.validation.required")),
    },
    [["titleFi", "titleEn"]]
  )
}

const GuideModal: React.FC<GuideModalProps> = ({ data = null, show, onSubmit, onCancel }) => {
  const { t } = useTranslation()

  return (
    <Modal show={show} onHide={onCancel} backdrop="static">
      <Formik<GuideFormValues>
        key={"Formik-edit-guide"}
        enableReinitialize={true}
        initialValues={{
          icon: data?.icon || "body",
          titleEn: data?.titleEn || "",
          titleFi: data?.titleFi || "",
          isDraft: data?.isDraft || false,
        }}
        validationSchema={validationSchema(t)}
        onSubmit={values => onSubmit(values as unknown as Record<string, string>)}>
        {({ handleSubmit, ...props }) => (
          <Form noValidate onSubmit={handleSubmit}>
            <Modal.Header closeButton>
              <Modal.Title>{data ? t("guides.modal.title.edit") : t("guides.modal.title.create")}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Form.Row>
                <IconPicker
                  name="icon"
                  values={props.values}
                />
              </Form.Row>
              <Form.Row>
                <Form.Group as={Col} xs={12} controlId="AddNewGuideFormtitleFi" className="position-relative">
                  <Form.Text className="text-muted">{t("guides.form.titleFi")}</Form.Text>
                  <Form.Control
                    type="text"
                    name="titleFi"
                    placeholder=""
                    onChange={props.handleChange}
                    onBlur={props.handleBlur}
                    defaultValue={props.values.titleFi}
                    isInvalid={!!props.touched.titleFi && !!props.errors.titleFi}
                  />
                  <Form.Control.Feedback tooltip type="invalid">
                    {props.errors.titleFi}
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group as={Col} xs={12} controlId="AddNewGuideFormtitleEn" className="position-relative">
                  <Form.Text className="text-muted">{t("guides.form.titleEn")}</Form.Text>
                  <Form.Control
                    type="text"
                    name="titleEn"
                    placeholder=""
                    onChange={props.handleChange}
                    onBlur={props.handleBlur}
                    defaultValue={props.values.titleEn}
                    isInvalid={!!props.touched.titleEn && !!props.errors.titleEn}
                  />
                  <Form.Control.Feedback tooltip type="invalid">
                    {props.errors.titleEn}
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group as={Col} xs={12} controlId="AddNewGuideFormIsDraft" className="position-relative">
                  <Form.Text className="text-muted">{t("guides.form.isDraft")}</Form.Text>
                  <Form.Check
                    type="switch"
                    id="isDraft"
                    name="isDraft"
                    onChange={e => props.setFieldValue("isDraft", !e.target.checked)}
                    checked={!props.values.isDraft}
                    label={props.values.isDraft ? t("guides.form.draft") : t("guides.form.publish")}
                  />
                </Form.Group>
              </Form.Row>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" className="btn-default" onClick={onCancel}>
                {t("guides.button.cancel")}
              </Button>
              <Button variant="primary" className="btn-default" type="submit">
                {t("guides.button.save")}
              </Button>
            </Modal.Footer>
          </Form>
        )}
      </Formik>
    </Modal>
  )
}

export default GuideModal
